// Connections and journeys: flights that follow one another within a day are one journey
// (LGA → ORD → NRT → SGN), with a layover between each pair. Used by the Travel list, the
// booking page and the check-in reminder. Pure.

import type { Segment } from './types.ts';
import { toInstant } from './time.ts';
import { DEFAULT_HOME_CODES } from './stops.ts';

/** Longer than this between landing and the next departure is a stop, not a layover. */
export const MAX_LAYOVER_MINUTES = 24 * 60;
/** Under this, the layover is shown as tight. */
export const TIGHT_LAYOVER_MINUTES = 60;

export interface Connection {
  from: Segment;
  to: Segment;
  minutes: number;
  /** False when the next flight leaves from a different airport (lands JFK, leaves LGA). */
  sameAirport: boolean;
}

const byStart = (a: Segment, b: Segment) => toInstant(a.start).getTime() - toInstant(b.start).getTime();

/** Active flights in departure order. */
function flights(segments: Segment[]): Segment[] {
  return segments.filter((s) => s.type === 'flight' && s.status === 'active').sort(byStart);
}

/**
 * Every layover between consecutive flights (needs the first flight's landing time). Landing at
 * a home airport ends the journey: a night at home before the next trip is not a layover.
 */
export function connectionsOf(segments: Segment[], homeCodes: string[] = DEFAULT_HOME_CODES): Connection[] {
  const fl = flights(segments);
  const home = homeCodes.map((c) => c.toUpperCase());
  const out: Connection[] = [];
  for (let i = 1; i < fl.length; i++) {
    const [a, b] = [fl[i - 1], fl[i]];
    if (!a.end?.time || !b.start.time || home.includes(a.to?.code?.toUpperCase() ?? '')) continue;
    const minutes = Math.round((toInstant(b.start).getTime() - toInstant(a.end).getTime()) / 60_000);
    if (minutes <= 0 || minutes > MAX_LAYOVER_MINUTES) continue;
    out.push({ from: a, to: b, minutes, sameAirport: !!a.to?.code && a.to.code === b.from?.code });
  }
  return out;
}

/** Flights grouped into journeys: each a run of flights joined by layovers. */
export function journeysOf(segments: Segment[], homeCodes?: string[]): Segment[][] {
  const fl = flights(segments);
  const joined = new Set(connectionsOf(fl, homeCodes).map((c) => c.to.id));
  const out: Segment[][] = [];
  for (const f of fl) {
    if (joined.has(f.id) && out.length) out.at(-1)!.push(f);
    else out.push([f]);
  }
  return out;
}

/** "1 h 27 m", "45 m", "26 h 05 m". */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m`;
}

/** "1 h 27 m layover in Chicago (ORD)" or "Change airports: JFK → LGA · 3 h 10 m". */
export function layoverText(c: Connection): string {
  if (!c.sameAirport) return `Change airports: ${c.from.to?.code ?? '?'} → ${c.to.from?.code ?? '?'} · ${formatMinutes(c.minutes)}`;
  const code = c.from.to?.code ?? '?';
  const city = c.from.to?.city;
  return `${formatMinutes(c.minutes)} layover in ${city ? `${city} (${code})` : code}`;
}

/** Tight connections and airport changes are shown in orange. */
export function needsCare(c: Connection): boolean {
  return !c.sameAirport || c.minutes < TIGHT_LAYOVER_MINUTES;
}
